import api from './apiClient';

class VerificationsApi {
  // Get all verifications for the current user
  async getUserVerifications() {
    try {
      const data = await api.get('/verifications/users/me/all');
      return data.data || [];
    } catch (error: unknown) {
      throw error;
    }
  }

  // Get verifications for a specific company
  async getUserCompanyVerifications(userId: string, companyId: string) {
    try {
      const data = await api.get(`/verifications/users/${userId}/companies/${companyId}`);
      return data.data;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Submit verification
  async submitVerification(companyId: string, verificationId: string, data: {
    documentUrl: string;
    certificateNumber?: string;
    expiryDate?: string;
    notes?: string;
  }) {
    try {
      const result = await api.post(`/verifications/companies/${companyId}/verifications/${verificationId}/submit`, data);
      return result.data;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Check if user has pending verifications
  async hasPendingVerifications() {
    try {
      const verifications = await this.getUserVerifications();

      return verifications.some((companyVer: any) => {
        const { verifications } = companyVer;
        return verifications.pending > 0 || verifications.expired > 0 ||
               verifications.completed < verifications.required;
      });
    } catch (error: unknown) {
      return false;
    }
  }

  // Company verifications CRUD operations

  // Get all verifications for a company. Includes both active and
  // soft-deleted (isActive=false) rules so the FE can surface
  // deactivated ones with their 72hr grace countdown.
  async getCompanyVerifications(companyId: string) {
    try {
      const data = await api.get(`/verifications/companies/${companyId}`, {
        isActive: 'all',
      });
      return data.data || [];
    } catch (error: unknown) {
      throw error;
    }
  }

  // Create new verification for a company
  async createCompanyVerification(companyId: string, verification: {
    name: string;
    type: string;
    isRequired: boolean;
    validityPeriod?: number;
    category?: string;
    description?: string;
    appliesFromDate?: string | null;
    appliesTo?: 'internal' | 'provider' | 'both';
    expiresOn?: string | null;
    scopedProviderCompanies?: string[];
    excludedProviderCompanyIds?: string[];
    requireFromCompanyAdmin?: boolean;
    verificationLevel?: 'individual' | 'company';
  }) {
    try {
      const data = await api.post(`/verifications/companies/${companyId}`, verification);
      return data.data;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Update verification
  async updateVerification(verificationId: string, updates: {
    name?: string;
    type?: string;
    isRequired?: boolean;
    validityPeriod?: number;
    category?: string;
    description?: string;
    appliesFromDate?: string | null;
    appliesTo?: 'internal' | 'provider' | 'both';
    expiresOn?: string | null;
    scopedProviderCompanies?: string[];
    excludedProviderCompanyIds?: string[];
    includedProviderCompanyIds?: string[];
    requireFromCompanyAdmin?: boolean;
    verificationLevel?: 'individual' | 'company';
    requiresRenewal?: boolean;
    recurrence?: { type: 'day_of_month'; day: number } | null;
  }) {
    try {
      const data = await api.put(`/verifications/${verificationId}`, updates);
      return data.data;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Deactivate (soft delete) a verification — starts the 72hr grace
  // window. Use deleteVerification(verificationId) for the legacy
  // company-context-less route or this one for the explicit company
  // path. Either way it sets isActive: false.
  async deleteVerification(verificationId: string) {
    try {
      await api.delete(`/verifications/${verificationId}`);
      return true;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Deactivate via the company-scoped route. Preferred when the caller
  // has the companyId on hand — keeps the audit + 403 messages
  // aligned with the canonical resource.
  async deactivateVerification(companyId: string, verificationId: string) {
    await api.delete(
      `/verifications/companies/${companyId}/verifications/${verificationId}`,
    );
    return true;
  }

  // Reactivate a soft-deleted verification (within or past the 72hr
  // grace window — as long as it hasn't been hard-deleted yet).
  async reactivateVerification(companyId: string, verificationId: string) {
    const data = await api.post(
      `/verifications/companies/${companyId}/verifications/${verificationId}/reactivate`,
    );
    return data.data;
  }

  // Hard-delete (permanent): wipes the rule, every submission, and
  // every S3 file. Only super_admin / safety_staff can call. `force`
  // bypasses the 72hr grace.
  async hardDeleteVerification(
    companyId: string,
    verificationId: string,
    opts?: { note?: string; force?: boolean },
  ) {
    const data = await api.delete(
      `/verifications/companies/${companyId}/verifications/${verificationId}/permanent`,
      { data: { note: opts?.note, force: opts?.force ?? false } },
    );
    return data;
  }

  // Turn a rule ON/OFF for ONE specific user without affecting the rest
  // of the company/provider it otherwise applies to — see
  // CompanyVerification.excludedUserIds on the BE. Not valid for
  // verificationLevel:'company' rules (whole-company documents have no
  // per-user exclusion).
  async toggleUserExclusion(
    companyId: string,
    verificationId: string,
    userId: string,
    excluded: boolean,
  ) {
    const data = await api.patch(
      `/verifications/companies/${companyId}/verifications/${verificationId}/user-exclusion`,
      { userId, excluded },
    );
    return data.data;
  }

  // Force a rule ON for ONE specific user who wouldn't otherwise get it by
  // scope (appliesTo/scopedProviderCompanies) — see
  // CompanyVerification.includedUserIds on the BE. Mutually exclusive with
  // toggleUserExclusion for the same user (setting one clears the other).
  async toggleUserInclusion(
    companyId: string,
    verificationId: string,
    userId: string,
    included: boolean,
  ) {
    const data = await api.patch(
      `/verifications/companies/${companyId}/verifications/${verificationId}/user-inclusion`,
      { userId, included },
    );
    return data.data;
  }

  // Platform-wide summary: active client companies fully compliant with
  // their required verifications, grouped by rubro (Company.industry).
  // super_admin / safety_staff only.
  async getComplianceByRubro(): Promise<{
    rubros: Array<{
      rubro: string;
      totalCompanies: number;
      compliantCompanies: number;
      nonCompliantCompanies: number;
      complianceRate: number;
    }>;
    generatedAt: string;
    // Tope defensivo de 500 empresas del lado del BE — si se llenó, este
    // resumen platform-wide quedó incompleto (PERF-3, audit 2026-09-08).
    truncated: boolean;
  }> {
    const data = await api.get('/verifications/compliance-by-rubro');
    return { ...data.data, truncated: !!data.pagination?.truncated };
  }

  // PDF version of getComplianceByRubro — same underlying computation.
  async exportComplianceByRubroPdf(): Promise<Blob> {
    return api.getBlob('/exports/verification-compliance?groupBy=rubro');
  }

  // Same platform-wide summary as getComplianceByRubro, one row per
  // company instead of grouped by industry. super_admin / safety_staff only.
  async getComplianceByCompany(): Promise<{
    companies: Array<{
      companyId: string;
      companyName: string;
      industry: string;
      totalUsers: number;
      compliantUsers: number;
      partialUsers: number;
      nonCompliantUsers: number;
      isCompliant: boolean;
      complianceRate: number;
    }>;
    generatedAt: string;
    // Ver nota de truncated en getComplianceByRubro (PERF-3, audit 2026-09-08).
    truncated: boolean;
  }> {
    const data = await api.get('/verifications/compliance-by-company');
    return { ...data.data, truncated: !!data.pagination?.truncated };
  }

  // PDF version of getComplianceByCompany — same underlying computation.
  async exportComplianceByCompanyPdf(): Promise<Blob> {
    return api.getBlob('/exports/verification-compliance?groupBy=company');
  }

  // Get company verification statistics
  async getCompanyVerificationStats(companyId: string) {
    try {
      const data = await api.get(`/verifications/companies/${companyId}/stats`);
      return data.data;
    } catch (error: unknown) {
      throw error;
    }
  }

  // Contraparte global (todas las empresas) de exportCompanyVerificationsReport
  // scope='company' — pestaña "De Empresa" del dashboard global.
  async exportGlobalProviderVerificationsPdf(): Promise<Blob> {
    return api.getBlob('/exports/verifications-report/global');
  }

  // Reporte PDF de verificaciones de UNA empresa — scope 'company' (Nivel
  // Empresa: documentos de toda la empresa proveedora) o 'individual'
  // (Nivel Individual: por persona, propia o prestada por un proveedor).
  // `providerIds`/`format` only apply to scope='company': narrow the report
  // to specific linked providers, and choose 'summary' (grouped by provider
  // / by rule, stays presentable with hundreds of providers) vs 'list' (one
  // row per document, the original report).
  async exportCompanyVerificationsReport(
    companyId: string,
    scope: 'company' | 'individual',
    opts?: { providerIds?: string[]; format?: 'summary' | 'list' },
  ): Promise<Blob> {
    const params = new URLSearchParams({ scope });
    if (opts?.providerIds?.length) params.set('providerIds', opts.providerIds.join(','));
    if (opts?.format) params.set('format', opts.format);
    return api.getBlob(
      `/exports/companies/${companyId}/verifications-report?${params.toString()}`,
    );
  }
}

export const verificationsApi = new VerificationsApi();
